const baseUrl = import.meta.env.VITE_HOSTING_BFF_BASE_URL;

if (!baseUrl) {
    throw new Error("VITE_HOSTING_BFF_BASE_URL is required.");
}

export type SiteStatus = "creating" | "building" | "running" | "stopped" | "failed" | "deleting";

export interface HostedSite {
    id: string;
    tenantId: string;
    name: string;
    subdomain: string;
    runtimeType: string;
    nodeVersion: string;
    gitRepoUrl: string;
    startCommand: string | null;
    status: SiteStatus;
    lastError: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface EnvVar {
    key: string;
    value: string;
}

export interface CreateSiteRequest {
    name: string;
    subdomain: string;
    gitRepoUrl: string;
    nodeVersion?: string;
    startCommand?: string;
    envVars?: EnvVar[];
}

async function fetchJson<T>(path: string, accessToken: string | undefined, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set("Accept", "application/json");
    if (init.body) headers.set("Content-Type", "application/json");
    if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
    const res = await fetch(new URL(path, baseUrl).toString(), { ...init, headers, credentials: "omit" });
    if (!res.ok) {
        let message = `Hosting BFF ${path} → ${res.status} ${res.statusText}`;
        try {
            const body = (await res.json()) as { title?: string; detail?: string };
            if (body.title || body.detail) {
                message = body.detail ? `${body.title ?? ""}: ${body.detail}` : body.title!;
            }
        } catch { /* non-JSON response */ }
        throw new Error(message);
    }
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
}

export const hostingApi = {
    listSites: (token: string | undefined) => fetchJson<HostedSite[]>("/api/hosting/sites", token),
    getSite: (id: string, token: string | undefined) =>
        fetchJson<HostedSite>(`/api/hosting/sites/${encodeURIComponent(id)}`, token),
    createSite: (req: CreateSiteRequest, token: string | undefined) =>
        fetchJson<HostedSite>("/api/hosting/sites", token, { method: "POST", body: JSON.stringify(req) }),
    redeploySite: (id: string, token: string | undefined) =>
        fetchJson<void>(`/api/hosting/sites/${encodeURIComponent(id)}/redeploy`, token, { method: "POST" }),
    restartSite: (id: string, token: string | undefined) =>
        fetchJson<void>(`/api/hosting/sites/${encodeURIComponent(id)}/restart`, token, { method: "POST" }),
    deleteSite: (id: string, token: string | undefined) =>
        fetchJson<void>(`/api/hosting/sites/${encodeURIComponent(id)}`, token, { method: "DELETE" }),
    getLogs: (id: string, token: string | undefined) =>
        fetchJson<string>(`/api/hosting/sites/${encodeURIComponent(id)}/logs`, token),
    listEnvVars: (id: string, token: string | undefined) =>
        fetchJson<EnvVar[]>(`/api/hosting/sites/${encodeURIComponent(id)}/env`, token),
    replaceEnvVars: (id: string, envVars: EnvVar[], token: string | undefined) =>
        fetchJson<void>(`/api/hosting/sites/${encodeURIComponent(id)}/env`, token, {
            method: "PUT",
            body: JSON.stringify(envVars)
        })
};
